import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getListOfAccts } from "../../../../store/actions";
import { getListOfOrgsAndAccts } from "../../../../store/auth/orgAcct/actions";

const withoutInitAccounts = (accounts) =>
  (accounts || []).filter(
    (a) => a?.accountNumber && !String(a.accountNumber).includes("INIT")
  );

/**
 * Org cloud accounts from getOrgsAndAccts (orgAccount + acctList stores).
 * Same source as Configuration → Cloud Accounts and IAM Stack tab.
 */
export function useCloudAccounts({ fetchIfMissing = true } = {}) {
  const dispatch = useDispatch();
  const acctList = useSelector((s) => s.acctList);
  const orgAccount = useSelector((s) => s.orgAccount);
  const orgAccounts = orgAccount?.accounts;
  const acctListAccounts = acctList?.accounts;

  useEffect(() => {
    if (!fetchIfMissing) return;
    if (!acctList?.accounts && !acctList?.loading) {
      dispatch(getListOfAccts());
    }
    if (
      (!orgAccount?.accounts || !orgAccount?.organizations?.length) &&
      !orgAccount?.loading
    ) {
      dispatch(getListOfOrgsAndAccts());
    }
  }, [
    dispatch,
    fetchIfMissing,
    acctList?.accounts,
    acctList?.loading,
    orgAccount?.accounts,
    orgAccount?.organizations,
    orgAccount?.loading,
  ]);

  const accounts = useMemo(() => {
    const source =
      orgAccounts?.length > 0 ? orgAccounts : acctListAccounts || [];
    return withoutInitAccounts(source);
  }, [orgAccounts, acctListAccounts]);

  const accountFilterOptions = useMemo(
    () =>
      [...accounts]
        .map((a) => ({
          value: String(a.accountNumber),
          label: a.accountName
            ? `${a.accountName} (${a.accountNumber})`
            : String(a.accountNumber),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts]
  );

  const loading = Boolean(orgAccount?.loading || acctList?.loading);

  return { accounts, accountFilterOptions, loading };
}

export default useCloudAccounts;
